import { api, ApiCallOptions } from '~/api';
import { Filter, SearchResponse, Sort } from '~/search.ts';
import {
    BankAccount,
    BankAccountMethod,
    BankAccountService,
    ContractorInvoiceDetails,
    ContractorInvoiceStatus,
    Currency,
    Expense,
    InvoiceLine,
    LineKind,
    PaymentStatus,
} from '~/types/domain.ts';
import {
    GetContractorResponse,
    GetPaymentResponse,
    OnboardingContractor,
    Contractor as PlatformContractor,
} from '~/types/platform.ts';
import { ucfirst } from '~/utils/format';

export function getContractor(id: number): Promise<GetContractorResponse> {
    return api<GetContractorResponse>(`/admin/contractors/${id}`);
}

export const terminationReasons: Record<string, string> = {
    acquisition: 'Acquisition',
    performance: 'Bad performance',
    shutdown: 'Company shutdown',
    management: 'Poor engineering management',
    restructuring: 'Restructuring / Layoffs',
    provisional: 'Short Term Period Ended',
    recruited: 'Accepted an inbound offer',
    unmotivated: 'Job not appealing anymore',
    cancelled: 'Never got to start',
    personal: 'Personal reasons',
    solicited: 'Solicited by client',
};

export function getTerminationReason(reason: string): string {
    return terminationReasons[reason] || ucfirst(reason);
}

const call = <T>(path: string, opts: ApiCallOptions = {}) =>
    api<T>('/admin/contractors' + path, opts);

export type Contractor = PlatformContractor & {
    company_fee: number;
    address: string;
    birthday: string;
    severance?: string;
    days_owed?: number;
    pto_payout?: string;
    severance_total?: string;
};

export function getContractors(
    filters: { filters: Filter[]; sort: Sort | Sort[] },
    page = 1,
    perPage = 50,
    apiCallOpts: ApiCallOptions = {},
): Promise<SearchResponse<Contractor>> {
    return call<SearchResponse<Contractor>>('/search', {
        method: 'POST',
        body: {
            filters: filters.filters,
            sort: [filters.sort].flat(),
            page,
            per_page: perPage,
        },
        ...apiCallOpts,
    });
}

export type ContractorMonthInvoice = ContractorInvoiceDetails & {
    has_account: boolean;
    account_id: number;
    account_holder: string;
    is_edited: boolean;
    account_updated_last_month: boolean;
    has_expenses: boolean;
    started_this_month: boolean;
    ends_this_month: boolean;
    has_raises: boolean;
    has_bonuses: boolean;
    contract_id?: number;
    fee: string;
    insurance: string;
    expenses: string;
    type: string;
    service?: BankAccountService;
    method?: BankAccountMethod;
    contracting_entity_country?: string;
    payment_status?: PaymentStatus;
    payment_id?: number;
    payment_receipt?: string;
};

export type MonthInvoicesResponse = {
    invoices: ContractorMonthInvoice[];
    total: {
        fees: string;
        insurance: string;
        expenses: string;
        total: string;
        us_money: string;
        international_money: string;
        status: Partial<Record<ContractorInvoiceStatus, string>>;
    };
};

export function getMonthInvoices(
    month: number,
    year: number,
    customer: number | null = null,
): Promise<MonthInvoicesResponse> {
    const search = customer ? `?customer=${customer}` : '';
    return api<MonthInvoicesResponse>(`/admin/invoices/${year}/${month}${search}`);
}

export type ContractorInvoice = {
    id: number;
    status: ContractorInvoiceStatus;
    error_description: string | null;
    month: number;
    year: number;
    amount: number;
    currency: Currency;
    path_at: string | null;
    number: string;
    from?: string;
    to?: string;
    user_id?: number;
    issue_date?: string;
    due_date?: string;
    lines: InvoiceLine[];
    url?: string;
    account?: BankAccount;
};

export function getContractorInvoice(id: number): Promise<ContractorInvoice> {
    return api<ContractorInvoice>(`/admin/invoices/${id}`);
}

type UpdateInvoiceRequest = {
    lines: {
        id: number;
        kind: LineKind;
        concept: string;
        quantity: number;
        rate: number;
    }[];
    deleted: number[];
};

export function updateContractorInvoice(id: number, data: UpdateInvoiceRequest): Promise<boolean> {
    return api<boolean>(`/admin/invoices/${id}`, { method: 'PATCH', body: data });
}

export async function exportContractorMonthInvoices(
    year: number,
    month: number,
    customer: number | null = null,
): Promise<Blob> {
    const search = customer ? `?customer=${customer}` : '';
    return await api.raw(`/admin/invoices/${year}/${month}/csv${search}`).then((r) => r.blob());
}

export async function exportContractorMonthPayments(year: number, month: number): Promise<Blob> {
    return await api.raw(`/admin/invoices/${year}/${month}/payments/csv`).then((r) => r.blob());
}

export async function exportContractorInvoices(id: number): Promise<Blob> {
    return await api.raw(`/admin/invoices/${id}/csv`).then((r) => r.blob());
}

type TerminationCalculation = {
    services: string;
    termination_fee: string;
    unused_pto: string;
    unused_pto_days: number;
    total_termination: string;
};

export type TerminationDetailsInput = {
    termination_fee?: number | string | null;
    unused_pto_days?: number | string | null;
};

export async function calculateTermination(
    contractId: number,
    terminatedAt: string | Date,
    resigned: boolean,
    reason: string | undefined,
    details: TerminationDetailsInput,
): Promise<TerminationCalculation> {
    return call<TerminationCalculation>(`/calculate_termination`, {
        method: 'POST',
        body: {
            contract_id: contractId,
            terminated_at: terminatedAt,
            resigned,
            reason,
            details,
        },
    });
}

export async function terminate(
    contractId: number,
    terminatedAt: string | Date,
    resigned: boolean,
    reason: string | undefined,
    equipmentKept: number[] = [],
    details: TerminationDetailsInput,
): Promise<boolean> {
    return call<boolean>(`/terminate`, {
        method: 'POST',
        body: {
            contract_id: contractId,
            terminated_at: terminatedAt,
            resigned,
            reason,
            equipment_kept: equipmentKept,
            details,
        },
    });
}

export async function terminationPdf(
    contractId: number,
    terminatedAt: string | Date,
    resigned: boolean,
    reason: string | undefined,
    equipmentKept: number[] = [],
    details: TerminationDetailsInput,
): Promise<Blob> {
    return api
        .raw(`/admin/contractors/termination_pdf`, {
            method: 'POST',
            body: JSON.stringify({
                contract_id: contractId,
                terminated_at: terminatedAt,
                resigned,
                reason,
                equipment_kept: equipmentKept,
                details,
            }),
            headers: { 'Content-Type': 'application/json', Accept: 'application/pdf' },
        })
        .then((r) => r.blob());
}

type File = {
    id: number;
    name: string;
    url: string;
};

type CreateDocumentRequest = {
    name: string;
    document: File;
};

export async function createDocument(
    userId: number,
    data: CreateDocumentRequest,
): Promise<boolean> {
    return api<boolean>('/admin/agreements/upload', {
        method: 'POST',
        body: {
            ...data,
            user_id: userId,
        },
    });
}

export async function remindInvoice(id: number): Promise<boolean> {
    return api<boolean>(`/admin/invoices/${id}/remind`, {
        method: 'POST',
    });
}

export type ContractorExpense = Expense & {
    company_name?: string;
    contractor?: string;
    total: number;
    reviewer?: string;
    manager?: string;
    invoice?: File;
};

export async function getExpenses(
    id: number,
    page: number,
): Promise<SearchResponse<ContractorExpense>> {
    return call<SearchResponse<ContractorExpense>>(`/${id}/expenses?page=${page}`);
}

export async function getOnboardingContractors(all = false): Promise<OnboardingContractor[]> {
    return api<OnboardingContractor[]>(`/contractors/onboarding${all ? '?all=true' : ''}`);
}

export async function getPayment(id: number): Promise<GetPaymentResponse> {
    return api<GetPaymentResponse>(`/admin/invoices/payments/${id}`);
}
